/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from "@angular/common";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";

import { QPilotService } from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { TimezoneHelper } from "../../../../core/utils/timezone.helper";
import {
  ScheduledOrderEventLog,
  ScheduledOrderHistoryDialogData,
} from "../../interfaces/processing-history.interface";

/**
 * Scheduled Order History Dialog Component
 * Shows the event logs history for a scheduled order (matches old QMC ScheduledOrderHistoryDialogComponent)
 */
@Component({
  selector: "app-scheduled-order-history-dialog",
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: "./scheduled-order-history-dialog.component.html",
  styleUrl: "./scheduled-order-history-dialog.component.scss",
})
export class ScheduledOrderHistoryDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  logs: ScheduledOrderEventLog[] = [];
  isLoading = false;
  typeFilter: "all" | "success" | "info" | "warning" | "error" = "all";

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private timezoneHelper: TimezoneHelper,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<ScheduledOrderHistoryDialogData>,
  ) {}

  ngOnInit(): void {
    this.loadHistory();
  }

  /**
   * Get access token from config or session
   */
  private getAccessToken() {
    return this.config.data?.accessToken ?? this.sessionService.accessToken;
  }

  /**
   * Load scheduled order event logs
   */
  private loadHistory(): void {
    const accessToken = this.getAccessToken();
    if (!this.config.data?.scheduledOrderId || !accessToken) {
      return;
    }

    this.isLoading = true;

    this.qpilotService
      .getScheduledOrderEventLogs(
        this.config.data.scheduledOrderId,
        accessToken,
        true, // showGlobalLoader
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (logs) => {
          // Sort by EventUtc descending (most recent first) - matches old QMC
          this.logs = logs
            .map((log) => ({
              ...log,
              EventUtc: new Date(log.EventUtc),
            }))
            .sort((a, b) => b.EventUtc.getTime() - a.EventUtc.getTime());

          this.isLoading = false;
        },
        error: (error) => {
          console.error("Failed to load scheduled order history:", error);
          this.isLoading = false;
        },
      });
  }

  /**
   * Get filtered logs based on type filter
   */
  get filteredLogs(): ScheduledOrderEventLog[] {
    if (this.typeFilter === "all") {
      return this.logs;
    }
    return this.logs.filter((log) => {
      const severity = this.getLogSeverity(log);
      // Handle "error" filter matching "danger" severity
      if (this.typeFilter === "error") {
        return severity === "danger";
      }
      return severity === this.typeFilter;
    });
  }

  /**
   * Toggle type filter
   */
  toggleFilter(filter: "all" | "success" | "info" | "warning" | "error"): void {
    // If clicking the same filter, reset to 'all'
    if (this.typeFilter === filter) {
      this.typeFilter = "all";
    } else {
      this.typeFilter = filter;
    }
  }

  /**
   * Get type statistics for display
   */
  getTypeStats(): {
    total: number;
    success: number;
    info: number;
    warning: number;
    error: number;
  } {
    const total = this.logs.length;
    const success = this.logs.filter(
      (log) => this.getLogSeverity(log) === "success",
    ).length;
    const info = this.logs.filter(
      (log) => this.getLogSeverity(log) === "info",
    ).length;
    const warning = this.logs.filter(
      (log) => this.getLogSeverity(log) === "warning",
    ).length;
    const error = this.logs.filter(
      (log) => this.getLogSeverity(log) === "danger",
    ).length;

    return { total, success, info, warning, error };
  }

  /**
   * Get the severity for log type display
   */
  getLogSeverity(
    log: ScheduledOrderEventLog,
  ): "success" | "info" | "warning" | "danger" {
    if (log.ExceptionType && log.ExceptionType !== "None") {
      return "danger";
    }

    switch (log.LogType?.toLowerCase()) {
      case "error":
        return "danger";
      case "warning":
        return "warning";
      case "info":
        return "info";
      default:
        return "success";
    }
  }

  /**
   * Get the icon for log type display
   */
  getLogIcon(log: ScheduledOrderEventLog): string {
    if (log.ExceptionType && log.ExceptionType !== "None") {
      return "pi pi-times-circle";
    }

    switch (log.LogType?.toLowerCase()) {
      case "error":
        return "pi pi-times-circle";
      case "warning":
        return "pi pi-exclamation-triangle";
      case "info":
        return "pi pi-info-circle";
      default:
        return "pi pi-check-circle";
    }
  }

  /**
   * Format the date for display
   */
  formatDate(date: Date): string {
    if (!date) return "-";
    // Use UTC offset of 0 for event logs (they are stored in UTC and should be displayed as-is)
    return this.timezoneHelper.formatDateWithTimezone(date, 0);
  }

  /**
   * Get the display text for log type
   */
  getLogTypeDisplay(log: ScheduledOrderEventLog): string {
    return log.LogType || (log.ExceptionType !== "None" ? "Error" : "Event");
  }

  /**
   * Get the formatted description with HTML support
   */
  getFormattedDescription(log: ScheduledOrderEventLog): string {
    return log.DescriptionFormatted || log.EventType || log.Description || "";
  }

  /**
   * Close the dialog
   */
  onClose(): void {
    this.ref.close();
  }

  /**
   * Track by function for ngFor performance
   */
  trackByLogId(index: number, log: ScheduledOrderEventLog): number {
    return log.Id;
  }

  /**
   * Get random width for skeleton log lines (for visual variety)
   */
  getRandomWidth(): string {
    const widths = ["75%", "85%", "92%", "68%", "80%", "88%", "95%", "70%"];
    return widths[Math.floor(Math.random() * widths.length)];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
